// Редактор распределений без разметки: матрица «комната × предмет» из
// ответа прогона ассетов (items.byRoom, CONTRACT.md §1d), рычаги клетки из
// схемы CLI и список «было → стало».
//
// Числа считает симуляция; здесь — только выбор среза и подписи. Единственный
// расчёт на месте — «шанс внутри таблицы» (вес строки / сумма весов): это
// разбор клетки, а не замена прогону.

export type EditValue = number | string;
export type Edits = Record<string, EditValue>;

export interface SchemaField {
  key: string;
  label: string;
  value: number;
  min: number;
  max: number;
  group: string;
  itemId?: string | null;
  category?: string | null;
}

export interface SchemaItem {
  id: string;
  displayName: string;
  category: string | null;
  kind?: string;
  enabled?: boolean;
}

export interface SchemaRoom {
  module: string;
  id: string;
  displayName: string;
  act?: string;
  lootPoints: number;
  preferredCategory: string;
  preferredCategoryKey: string;
}

export interface EditorSchema {
  budget: string;
  fields: SchemaField[];
  items: SchemaItem[];
  rooms: SchemaRoom[];
  lootCategories: string[];
}

export interface MatrixRoom {
  module: string;
  moduleId: string;
  displayName: string;
  presentShare: number;
  perRun: number;
}

export interface MatrixCell {
  module: string;
  itemId: string;
  source: string;
  share: number;
  shareWhenPresent: number;
  mean: number;
}

export interface RoomMatrix {
  runs: number;
  sources: string[];
  rooms: MatrixRoom[];
  cells: MatrixCell[];
}

export type Metric = 'share' | 'shareWhenPresent' | 'mean';

export const SOURCE_LABELS: Record<string, string> = {
  all: 'Все источники',
  floor: 'Лут уровня',
  drop: 'Дроп с трупов',
  fuse: 'Пробка лифта',
};

export const CATEGORY_LABELS: Record<string, string> = {
  ammo: 'Патроны',
  medkit: 'Медицина',
  battery: 'Батареи',
  key: 'Ключи',
  talons: 'Талоны',
  '': 'Без предпочтения',
};

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

export function normalizeSchema(payload: unknown): EditorSchema {
  const root = (payload ?? {}) as Record<string, unknown>;
  const list = <T>(value: unknown) => (Array.isArray(value) ? (value as T[]) : []);
  return {
    budget: String(root.budget ?? ''),
    fields: list<Record<string, unknown>>(root.fields)
      .filter((field) => typeof field.key === 'string' && num(field.value) !== null)
      .map((field) => ({
        key: String(field.key),
        label: String(field.label ?? field.key),
        value: field.value as number,
        min: num(field.min) ?? 0,
        max: num(field.max) ?? Math.max(10, (field.value as number) * 4),
        group: String(field.group ?? ''),
        itemId: typeof field.itemId === 'string' ? field.itemId : null,
        category: typeof field.category === 'string' ? field.category : null,
      })),
    items: list<SchemaItem>(root.items).filter((item) => typeof item?.id === 'string'),
    rooms: list<SchemaRoom>(root.rooms).filter((room) => typeof room?.module === 'string'),
    lootCategories: list<string>(root.lootCategories).filter((id) => typeof id === 'string'),
  };
}

export function normalizeMatrix(payload: unknown): RoomMatrix | null {
  const byRoom = ((payload ?? {}) as { items?: { byRoom?: unknown } }).items?.byRoom as Record<string, unknown> | null | undefined;
  if (!byRoom || !Array.isArray(byRoom.rooms) || !Array.isArray(byRoom.cells)) return null;
  return {
    runs: num(byRoom.runs) ?? 0,
    sources: Array.isArray(byRoom.sources) ? (byRoom.sources as string[]) : ['all'],
    rooms: byRoom.rooms as MatrixRoom[],
    cells: byRoom.cells as MatrixCell[],
  };
}

/** Только правки, которые понимает прогон этого бюджета (черновик общий на все бюджеты). */
export function overridesFor(schema: EditorSchema, edits: Edits): Edits {
  const keys = new Set([...schema.fields.map((field) => field.key), ...schema.rooms.map((room) => room.preferredCategoryKey)]);
  return Object.fromEntries(Object.entries(edits).filter(([key]) => keys.has(key)));
}

export interface MatrixView {
  rooms: MatrixRoom[];
  items: SchemaItem[];
  value: (module: string, itemId: string) => number;
  max: number;
}

/**
 * Срез матрицы: источник и метрика. Колонки — предметы, у которых в этом
 * срезе есть хоть одна ненулевая клетка; строки — комнаты, которые выпадали.
 */
export function matrixView(matrix: RoomMatrix, schema: EditorSchema, source: string, metric: Metric, query = ''): MatrixView {
  const values = new Map<string, number>();
  const usedItems = new Set<string>();
  for (const cell of matrix.cells) {
    if (cell.source !== source) continue;
    const value = cell[metric];
    if (value > 0) {
      values.set(`${cell.module}\u0000${cell.itemId}`, value);
      usedItems.add(cell.itemId);
    }
  }
  const catalog = new Map(schema.items.map((item) => [item.id, item]));
  const needle = query.trim().toLowerCase();
  const items = [...usedItems]
    .map((id) => catalog.get(id) ?? { id, displayName: '', category: null })
    .filter((item) => !needle || `${item.id} ${item.displayName}`.toLowerCase().includes(needle))
    .sort((a, b) => (a.category ?? '~').localeCompare(b.category ?? '~') || itemLabel(a).localeCompare(itemLabel(b), 'ru'));
  const shown = new Set(items.map((item) => item.id));
  // Одинаковое русское имя у разных модулей (два «Атриума») различаем по id модуля.
  const present = matrix.rooms.filter((room) => room.presentShare > 0);
  const nameCount = new Map<string, number>();
  for (const room of present) nameCount.set(room.displayName, (nameCount.get(room.displayName) ?? 0) + 1);
  const rooms = present.map((room) =>
    (nameCount.get(room.displayName) ?? 0) > 1 ? { ...room, displayName: `${room.displayName} · ${room.moduleId || room.module}` } : room,
  );
  let max = 0;
  for (const [key, value] of values) if (shown.has(key.split('\u0000')[1]!)) max = Math.max(max, value);
  return { rooms, items, value: (module, itemId) => values.get(`${module}\u0000${itemId}`) ?? 0, max };
}

export function itemLabel(item: Pick<SchemaItem, 'id' | 'displayName'>): string {
  return item.displayName || item.id;
}

export function formatValue(value: number, metric: Metric): string {
  if (value === 0) return '';
  if (metric === 'mean') return value >= 10 ? value.toFixed(0) : value >= 1 ? value.toFixed(1) : value.toFixed(2);
  const percent = value * 100;
  return percent >= 10 ? `${percent.toFixed(0)}%` : percent >= 1 ? `${percent.toFixed(1)}%` : `${percent.toFixed(2)}%`;
}

/** Значение поля с учётом правок. */
export function currentValue(field: SchemaField, edits: Edits): number {
  const edited = edits[field.key];
  return typeof edited === 'number' ? edited : field.value;
}

export interface WeightLever {
  field: SchemaField;
  table: string;
  /** Шанс строки при одном выборе из таблицы: вес / сумма весов таблицы (с правками). */
  shareInTable: number;
}

/** Веса строк таблиц лута, которые дают этот предмет, и шанс внутри своей таблицы. */
export function weightLevers(schema: EditorSchema, itemId: string, edits: Edits): WeightLever[] {
  const weights = schema.fields.filter((field) => /\._entries\[\d+\]\._weight$/.test(field.key) && field.key.startsWith('LootTableDef_'));
  const tableOf = (field: SchemaField) => field.key.slice(0, field.key.indexOf('.'));
  const totals = new Map<string, number>();
  for (const field of weights) totals.set(tableOf(field), (totals.get(tableOf(field)) ?? 0) + currentValue(field, edits));
  return weights
    .filter((field) => field.itemId === itemId)
    .map((field) => ({ field, table: tableOf(field), shareInTable: currentValue(field, edits) / (totals.get(tableOf(field)) || 1) }));
}

/** Категория, число находок которой игра считает от врагов, а не из `_min`/`_max` строки бюджета. */
export const AMMO_CATEGORY = 'ammo';

/**
 * Строки бюджета категории предмета: сколько таких находок на уровне.
 * У патронов `_min`/`_max` игра не читает (LootBudgetRow) — остаётся только пачка.
 */
export function budgetLevers(schema: EditorSchema, category: string | null): SchemaField[] {
  if (!category) return [];
  return schema.fields.filter((field) => field.category === category && /\._lootRows\[\d+\]\._/.test(field.key)
    && !(category === AMMO_CATEGORY && /\._(min|max)$/.test(field.key)));
}

/**
 * Рычаги числа патронов на этаже (LevelPopulator): потолок = врагов × `_averageShotsPerEnemy`,
 * выкладывается `_ammoBudgetPermille` ‰ потолка.
 */
export function ammoLevers(schema: EditorSchema, category: string | null): SchemaField[] {
  if (category !== AMMO_CATEGORY) return [];
  return ['_averageShotsPerEnemy', '_ammoBudgetPermille']
    .map((name) => schema.fields.find((field) => field.key.endsWith(`.${name}`)))
    .filter((field): field is SchemaField => field !== undefined);
}

/** Пачка с трупа для предмета (строки дроп-таблиц). */
export function dropStackLevers(schema: EditorSchema, itemId: string): SchemaField[] {
  return schema.fields.filter((field) => field.itemId === itemId && /\._entries\[\d+\]\._stack(Min|Max)$/.test(field.key));
}

export function preferenceBonusField(schema: EditorSchema): SchemaField | null {
  return schema.fields.find((field) => field.key.endsWith('._lootClassWeightBonus')) ?? null;
}

export function currentCategory(room: SchemaRoom, edits: Edits): string {
  const edited = edits[room.preferredCategoryKey];
  return typeof edited === 'string' ? edited : room.preferredCategory;
}

export interface ChangeLine {
  key: string;
  was: EditValue | null;
  now: EditValue;
}

/** «Было → стало» по всем правкам черновика; `was` — из схемы текущего бюджета, если поле там есть. */
export function changeLines(schema: EditorSchema, edits: Edits): ChangeLine[] {
  const fields = new Map(schema.fields.map((field) => [field.key, field.value]));
  const rooms = new Map(schema.rooms.map((room) => [room.preferredCategoryKey, room.preferredCategory]));
  return Object.entries(edits)
    .map(([key, now]) => ({ key, was: fields.get(key) ?? rooms.get(key) ?? null, now }))
    .filter((line) => line.was === null || line.was !== line.now)
    .sort((a, b) => a.key.localeCompare(b.key));
}

export function changesText(lines: ChangeLine[], author: string): string {
  const show = (value: EditValue | null) => (value === null ? '?' : value === '' ? '«нет»' : String(value));
  return [
    'СПУСК — черновик баланса',
    `Автор: ${author}`,
    'Значения для переноса в инспектор Unity (ассет · поле · было → стало):',
    '',
    ...lines.map((line) => {
      const dot = line.key.indexOf('.');
      return `${line.key.slice(0, dot)} · ${line.key.slice(dot + 1)} · ${show(line.was)} → ${show(line.now)}`;
    }),
  ].join('\n');
}

/** Поставить или снять правку: значение, равное ассету, — не правка. */
export function withEdit(edits: Edits, key: string, value: EditValue, original: EditValue): Edits {
  const next = { ...edits };
  if (value === original) delete next[key];
  else next[key] = value;
  return next;
}
